import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { useSession } from '../lib/auth'
import { Bouton, Champ, Saisie } from '../components/ui/Base'
import { MessageErreur } from '../components/ui/Etats'

const schema = z.object({
  email: z.string().min(1, 'Renseignez votre adresse électronique.').email('Adresse électronique invalide.'),
  mot_de_passe: z.string().min(1, 'Renseignez votre mot de passe.'),
})

type Formulaire = z.infer<typeof schema>

export function Connexion() {
  const { connecter, utilisateur } = useSession()
  const naviguer = useNavigate()
  const [parametres] = useSearchParams()
  const [erreur, setErreur] = useState<unknown>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulaire>({ resolver: zodResolver(schema) })

  if (utilisateur) return <Navigate to={parametres.get('suite') ?? '/planning'} replace />

  async function soumettre(valeurs: Formulaire) {
    setErreur(null)
    try {
      const profil = await connecter(valeurs.email, valeurs.mot_de_passe)
      // Un mot de passe à changer prime sur la destination demandée : sinon
      // l'utilisateur atterrit sur une page qu'il devra quitter aussitôt.
      naviguer(
        profil.doit_changer_mot_de_passe ? '/mot-de-passe/changer' : (parametres.get('suite') ?? '/planning'),
        { replace: true },
      )
    } catch (e) {
      setErreur(e)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">SoinsLib</h1>
          <p className="mt-1 text-sm text-slate-600">Gestion de cabinet d&apos;infirmiers libéraux</p>
        </div>

        <form
          onSubmit={handleSubmit(soumettre)}
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          noValidate
        >
          {erreur ? <MessageErreur erreur={erreur} /> : null}

          <Champ libelle="Adresse électronique" id="email" erreur={errors.email?.message} obligatoire>
            <Saisie
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              aria-invalid={Boolean(errors.email)}
              {...register('email')}
            />
          </Champ>

          <Champ libelle="Mot de passe" id="mot_de_passe" erreur={errors.mot_de_passe?.message} obligatoire>
            <Saisie
              id="mot_de_passe"
              type="password"
              autoComplete="current-password"
              aria-invalid={Boolean(errors.mot_de_passe)}
              {...register('mot_de_passe')}
            />
          </Champ>

          {/* Bouton désactivé pendant la mutation : une double soumission
              consomme deux fois le quota de tentatives du §8.5. */}
          <Bouton type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Connexion…' : 'Se connecter'}
          </Bouton>

          <p className="text-center text-sm">
            <Link to="/mot-de-passe/oubli" className="text-sky-700 hover:underline">
              Mot de passe oublié ?
            </Link>
          </p>
        </form>

        <p className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-center text-xs text-slate-500">
          Démonstration sur données fictives.
          <br />
          <span className="font-mono">arnaud@soinslib.demo</span> ·{' '}
          <span className="font-mono">Arnaud00000000</span>
        </p>
      </div>
    </div>
  )
}

